import { useMemo, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { ETAPES, ETAPE_INFO, calculerScore, deplacerFoyer, membresPresents, plageDe, verifierRepartition, type AffectationHote, type Alerte, type Etape, type Foyer, type Groupe, type Piste, type Score } from '@dinertournant/core';
import { BandeauAlerte, CarteGroupe, IconeActualiser, PastilleEtape } from '../../components/index.js';
import { useChargement } from '../../hooks/useChargement.js';
import { ErreurApi, api, apiPatch, dateLongue, heureCourte, nomComplet, type EtatPlanning } from '../../lib/api.js';
import type { ContexteEvenement } from './EvenementLayout.js';

interface DonneesRepartition {
  foyers: Foyer[];
  hotes: AffectationHote[];
  groupes: Groupe[];
  score: Score | null;
  alertes: Alerte[];
  planning: EtatPlanning;
}

interface ReponseGeneration {
  graine: string | number;
  groupes: Groupe[];
  score: Score;
  alertes: Alerte[];
  planning: EtatPlanning;
}

interface Echange {
  description: string;
  avant: Groupe[];
  doublonsAvant: number;
  deplaces: Set<string>;
}

type Filtre = 'toutes' | Etape;

/** Initiales pour l'en-tête de la matrice : « Claire Martin » → « CM ». */
function initiales(nom: string): string {
  return nom
    .split(/[\s-]+/)
    .filter(Boolean)
    .map((w) => w[0]!)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

/** Matrice « Qui rencontre qui » : pour chaque paire de personnes, les étapes partagées. */
function construireMatrice(foyers: Foyer[], groupes: Groupe[]) {
  const personnes: { id: string; nom: string; foyerId: string }[] = [];
  for (const f of foyers) for (const m of membresPresents(f)) personnes.push({ id: m.id, nom: m.prenomNom, foyerId: f.id });
  const parFoyer = new Map(foyers.map((f) => [f.id, membresPresents(f).map((m) => m.id)]));
  const rencontres = new Map<string, Etape[]>();
  const cle = (a: string, b: string) => (a < b ? `${a}|${b}` : `${b}|${a}`);
  for (const g of groupes) {
    const ids = g.foyerIds.flatMap((fid) => parFoyer.get(fid) ?? []);
    for (let i = 0; i < ids.length; i++)
      for (let j = i + 1; j < ids.length; j++) {
        const k = cle(ids[i]!, ids[j]!);
        rencontres.set(k, [...(rencontres.get(k) ?? []), g.etape]);
      }
  }
  return { personnes, rencontres: (a: string, b: string) => rencontres.get(cle(a, b)) ?? [] };
}

/** Écran 5 : résultat de la répartition, échange manuel, matrice. */
export function Repartition() {
  const { detail, recharger: rechargerEvenement } = useOutletContext<ContexteEvenement>();
  const eventId = detail.event.id;
  const { donnees, erreur, remplacer, recharger } = useChargement(() => api.get<DonneesRepartition>(`/api/events/${eventId}/repartition`), [eventId]);
  const [enCours, setEnCours] = useState(false);
  const [erreurAction, setErreurAction] = useState<string | null>(null);
  const [echange, setEchange] = useState<Echange | null>(null);
  const [filtre, setFiltre] = useState<Filtre>('toutes');
  const [matriceOuverte, setMatriceOuverte] = useState(true);
  const [obsoleteIgnore, setObsoleteIgnore] = useState(false);
  const [pistes, setPistes] = useState<Piste[] | null>(null);
  const [recherchePistes, setRecherchePistes] = useState(false);
  const [pisteEnCours, setPisteEnCours] = useState<number | null>(null);

  const foyers = donnees?.foyers ?? [];
  const groupes = donnees?.groupes ?? [];
  const parId = useMemo(() => new Map(foyers.map((f) => [f.id, f])), [foyers]);
  const cible = { min: detail.event.tailleMin ?? 3, max: detail.event.tailleMax ?? 5 };
  const plage = plageDe(cible);
  const score = useMemo(() => (groupes.length ? calculerScore(foyers, groupes, cible) : null), [foyers, groupes, cible.min, cible.max]);
  const alertes = useMemo(() => (groupes.length ? verifierRepartition(foyers, groupes, cible) : []), [foyers, groupes, cible.min, cible.max]);
  const participants = foyers.reduce((s, f) => s + membresPresents(f).length, 0);
  const horaires: Record<Etape, string> = { entree: detail.event.horaireEntree, plat: detail.event.horairePlat, dessert: detail.event.horaireDessert };
  const matrice = useMemo(() => (groupes.length && matriceOuverte ? construireMatrice(foyers, groupes) : null), [foyers, groupes, matriceOuverte]);

  const generer = async () => {
    if (!donnees) return;
    setErreurAction(null);
    setEnCours(true);
    try {
      const r = await api.post<ReponseGeneration>(`/api/events/${eventId}/repartition`, { graine: `${Date.now()}` });
      remplacer({ ...donnees, groupes: r.groupes, score: r.score, alertes: r.alertes, planning: r.planning });
      setEchange(null);
      setObsoleteIgnore(false);
      void rechargerEvenement();
    } catch (e) {
      setErreurAction(e instanceof ErreurApi ? e.message : 'Impossible de joindre le serveur.');
    } finally {
      setEnCours(false);
    }
  };

  const enregistrer = async (nouveaux: Groupe[]) => {
    if (!donnees) return;
    setErreurAction(null);
    try {
      const r = await api.put<{ groupes: Groupe[]; score: Score; alertes: Alerte[] }>(`/api/events/${eventId}/repartition`, { groupes: nouveaux });
      remplacer({ ...donnees, groupes: r.groupes, score: r.score, alertes: r.alertes });
    } catch (e) {
      setErreurAction(e instanceof ErreurApi ? e.message : 'Impossible de joindre le serveur.');
      await recharger();
    }
  };

  /** Échange manuel : le foyer (bloc) rejoint la table d'un autre hôte, recalcul instantané, enregistrement. */
  const deplacer = (etape: Etape, foyerId: string, versHoteFoyerId: string) => {
    if (!donnees || !score) return;
    let nouveaux: Groupe[];
    try {
      nouveaux = deplacerFoyer(groupes, etape, foyerId, versHoteFoyerId);
    } catch (e) {
      setErreurAction((e as Error).message);
      return;
    }
    const apres = calculerScore(foyers, nouveaux, cible);
    const f = parId.get(foyerId);
    const h = parId.get(versHoteFoyerId);
    const delta = apres.pairesEnDouble.length - (echange ? echange.doublonsAvant : score.pairesEnDouble.length);
    const base = echange ?? { avant: groupes, doublonsAvant: score.pairesEnDouble.length, deplaces: new Set<string>(), description: '' };
    const deplaces = new Set(base.deplaces);
    deplaces.add(`${etape}:${foyerId}`); // étiquette « déplacé » propre à l'étape
    const mouvement = `${f?.nom ?? 'une voiture'} ${f && membresPresents(f).length > 1 ? 'vont' : 'va'} chez ${h?.nom ?? '?'}`;
    const description = `${base.description ? `${base.description}, ` : `Échange manuel ${ETAPE_INFO[etape].au} : `}${mouvement}`;
    const indicateurs = delta > 0 ? `${delta} rencontre${delta > 1 ? 's' : ''} en double apparai${delta > 1 ? 'ssent' : 't'}.` : delta < 0 ? `${-delta} rencontre${-delta < -1 ? 's' : ''} en double disparai${-delta > 1 ? 'ssent' : 't'}.` : 'aucun nouveau doublon.';
    setEchange({ avant: base.avant, doublonsAvant: base.doublonsAvant, deplaces, description: `${description}. Indicateurs recalculés : ${indicateurs}` });
    remplacer({ ...donnees, groupes: nouveaux, score: apres });
    void enregistrer(nouveaux);
  };

  const chercherPistes = async () => {
    setErreurAction(null);
    setRecherchePistes(true);
    try {
      const r = await api.post<{ pistes: Piste[] }>(`/api/events/${eventId}/repartition/pistes`, {});
      setPistes(r.pistes);
    } catch (e) {
      setErreurAction(e instanceof ErreurApi ? e.message : 'Impossible de joindre le serveur.');
    } finally {
      setRecherchePistes(false);
    }
  };

  const appliquerPiste = async (p: Piste, index: number) => {
    if (!donnees) return;
    setErreurAction(null);
    setPisteEnCours(index);
    try {
      if (p.type === 'taille_table' && p.tailleMin && p.tailleMax) {
        await apiPatch(`/api/events/${eventId}/tailles`, { tailleMin: p.tailleMin, tailleMax: p.tailleMax });
        await rechargerEvenement();
        setPistes(null);
        return;
      }
      const r = await api.post<ReponseGeneration & { hotes: AffectationHote[] }>(`/api/events/${eventId}/repartition/pistes/appliquer`, { hotes: p.hotes, graine: p.graine });
      remplacer({ ...donnees, hotes: r.hotes, groupes: r.groupes, score: r.score, alertes: r.alertes, planning: r.planning });
      setEchange(null);
      setPistes(null);
      void rechargerEvenement();
    } catch (e) {
      setErreurAction(e instanceof ErreurApi ? e.message : 'Impossible de joindre le serveur.');
    } finally {
      setPisteEnCours(null);
    }
  };

  const annulerEchange = () => {
    if (!echange || !donnees) return;
    const avant = echange.avant;
    setEchange(null);
    remplacer({ ...donnees, groupes: avant, score: calculerScore(foyers, avant, cible) });
    void enregistrer(avant);
  };

  if (erreur) return <BandeauAlerte niveau="attention" role="alert" titre={erreur} />;
  if (!donnees) return <p className="texte-muet">Chargement…</p>;

  const tables = groupes.length;
  const etapesAffichees = ETAPES.filter((e) => filtre === 'toutes' || filtre === e);
  const planning = donnees.planning;

  return (
    <div className="page-app">
      <div className="entete-page">
        <div style={{ maxWidth: 720 }}>
          <h1 className="display">Répartition des convives</h1>
          <p className="texte-doux">
            {groupes.length
              ? `${participants} participants, ${tables} tables. Faites glisser un convive vers une autre table pour l'échanger : une voiture se déplace toujours d'un bloc.`
              : `${participants} participants. Le moteur place chaque voiture à une table par étape en cherchant à ce que deux personnes ne se retrouvent jamais ensemble deux fois.`}
          </p>
        </div>
        <div className="ligne" style={{ flexWrap: 'wrap' }}>
          <button type="button" className="btn" onClick={generer} disabled={enCours}>
            <IconeActualiser /> {groupes.length ? 'Relancer la répartition' : 'Répartir les convives'}
          </button>
          <Link to={`/app/e/${eventId}/planning`} className="btn principal" aria-disabled={!groupes.length}>
            Valider et créer le planning →
          </Link>
        </div>
      </div>

      {erreurAction ? (
        <BandeauAlerte niveau="attention" role="alert" titre={erreurAction} actions={<Link className="btn" to={`/app/e/${eventId}/hotes`}>Voir les hôtes par étape</Link>} />
      ) : null}

      {planning.planningObsolete && !obsoleteIgnore ? (
        <BandeauAlerte
          niveau="sombre"
          titre={`${planning.changements.length} changement${planning.changements.length > 1 ? 's' : ''} depuis la répartition${planning.repartitionGenereeAt ? ` du ${dateLongue(planning.repartitionGenereeAt.slice(0, 10))}` : ''}.`}
          actions={
            <>
              <button type="button" className="btn" onClick={generer} disabled={enCours}>
                Régénérer la répartition
              </button>
              <button type="button" className="btn secondaire" onClick={() => setObsoleteIgnore(true)}>
                Ajuster à la main
              </button>
            </>
          }
        >
          Les parcours déjà envoyés restent valables tant que vous ne régénérez pas.
          <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
            {planning.changements.map((c) => (
              <li key={c.foyerId}>{c.nom} : inscription modifiée.</li>
            ))}
          </ul>
        </BandeauAlerte>
      ) : null}
      {planning.planningObsolete && !obsoleteIgnore ? (
        <BandeauAlerte niveau="info">Après régénération, seuls les participants dont le parcours change recevront un nouvel e-mail.</BandeauAlerte>
      ) : null}

      {echange ? (
        <BandeauAlerte niveau="info" role="status" titre={echange.description} actions={<button type="button" className="btn" onClick={annulerEchange}>Annuler l'échange</button>} />
      ) : null}

      {!groupes.length ? (
        <div className="carte pile" style={{ maxWidth: 640 }}>
          <h2 style={{ fontSize: 18 }}>Aucune répartition pour l'instant</h2>
          <p className="texte-doux">Vérifiez d'abord les hôtes de chaque étape, puis lancez la répartition. Vous pourrez la relancer autant de fois que vous voulez et ajuster à la main.</p>
          <div className="ligne">
            <button type="button" className="btn principal" onClick={generer} disabled={enCours}>
              {enCours ? 'Calcul en cours…' : 'Répartir les convives'}
            </button>
            <Link className="btn" to={`/app/e/${eventId}/hotes`}>
              Hôtes par étape
            </Link>
          </div>
        </div>
      ) : null}

      {score ? (
        <>
          <div className="cartes-stats stats-repartition">
            <div className="carte stat">
              <span className="libelle">Rencontres uniques</span>
              <span className="valeur">{score.rencontresUniques}</span>
              <span className="detail">sur {score.rencontresTotales} rencontres au total</span>
              <div className="jauge" aria-hidden="true">
                <span style={{ width: `${score.rencontresTotales ? Math.round((score.rencontresUniques / score.rencontresTotales) * 100) : 0}%` }} />
              </div>
            </div>
            <div className={`carte stat${score.pairesEnDouble.length ? ' attention' : ''}`}>
              <span className="libelle">Rencontres en double</span>
              <span className="valeur">{score.pairesEnDouble.length}</span>
              <span className="detail">
                paires de personnes ensemble à deux étapes.
                {echange ? ` Possible : ${echange.doublonsAvant} (sans votre échange)` : ''}
              </span>
            </div>
            <div className={`carte stat${score.groupesHorsPlage ? ' attention' : ''}`}>
              <span className="libelle">Groupes hors plage {plage.min}–{plage.max}</span>
              <span className="valeur">{score.groupesHorsPlage}</span>
              <span className="detail">
                sur {tables} ·{' '}
                {score.groupesHorsPlage
                  ? 'à regarder sur les cartes signalées'
                  : (() => {
                      const tailles = [...new Set(score.groupes.map((g) => g.taille))].sort();
                      return tailles.length === 1 ? `tous les groupes comptent ${tailles[0]} personnes (plage ${plage.min} à ${plage.max})` : `groupes de ${tailles.join(' à ')} personnes (plage ${plage.min} à ${plage.max})`;
                    })()}
              </span>
            </div>
            <div className="carte stat">
              <span className="libelle">Voitures réunies</span>
              <span className="valeur">
                {score.foyersReunis.reunis} / {score.foyersReunis.total}
              </span>
              <span className="detail">les voitures de 2 ou 3 restent ensemble aux 3 étapes</span>
            </div>
          </div>

          {score.foyersEnDouble.length ? (
            <section className="recroise" aria-labelledby="recroise">
              <h2 id="recroise">Qui se recroise</h2>
              <ul>
                {score.foyersEnDouble.map((d) => {
                  const a = parId.get(d.foyerA);
                  const b = parId.get(d.foyerB);
                  return (
                    <li key={`${d.foyerA}-${d.foyerB}`}>
                      <span>
                        <strong>{a ? nomComplet(a) : '?'}</strong> et <strong>{b ? nomComplet(b) : '?'}</strong> · ensemble {d.lieux.map((l) => `${ETAPE_INFO[l.etape].au} (chez ${parId.get(l.hoteFoyerId)?.nom ?? '?'})`).join(' et ')}
                      </span>
                      <span className="nb">
                        {d.nombrePaires} paire{d.nombrePaires > 1 ? 's' : ''} concernée{d.nombrePaires > 1 ? 's' : ''}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </section>
          ) : null}

          {score.pairesEnDouble.length && !echange ? (
            <section className="carte pile pistes" aria-labelledby="pistes-titre">
              <div className="entete-page" style={{ alignItems: 'center' }}>
                <div>
                  <h2 id="pistes-titre" style={{ fontSize: 18 }}>
                    Pistes pour éviter les doublons
                  </h2>
                  <p className="texte-doux" style={{ fontSize: 15 }}>
                    Avec {participants} participants et {tables} tables, le moteur n'a pas trouvé de tirage sans doublon. Chaque piste ci-dessous est d'abord simulée, avec son résultat.
                  </p>
                </div>
                <button type="button" className="btn principal" onClick={chercherPistes} disabled={recherchePistes}>
                  {recherchePistes ? 'Simulation en cours…' : pistes ? 'Chercher à nouveau' : 'Chercher des pistes'}
                </button>
              </div>
              {pistes && pistes.length === 0 ? (
                <BandeauAlerte niveau="info" titre="Aucune piste simple ne fait mieux.">
                  Ni un autre tirage, ni un hôte en plus, en moins ou déplacé ne réduit les doublons. Il reste l'échange manuel sur les cartes, ou un volontaire supplémentaire à solliciter.
                </BandeauAlerte>
              ) : null}
              {pistes && pistes.length > 0 ? (
                <ol className="liste-pistes">
                  {pistes.map((p, i) => (
                    <li key={i} className={p.type}>
                      <span className="badge-doublons" aria-hidden="true">
                        {p.type === 'taille_table' ? '⚙' : p.doublonsApres}
                      </span>
                      <span className="texte">{p.description}</span>
                      <button type="button" className="btn" onClick={() => appliquerPiste(p, i)} disabled={pisteEnCours !== null}>
                        {pisteEnCours === i ? 'Application…' : 'Appliquer'}
                      </button>
                    </li>
                  ))}
                </ol>
              ) : null}
            </section>
          ) : null}

          {alertes
            .filter((a) => a.code !== 'rencontres_en_double')
            .map((a, i) => (
              <BandeauAlerte key={i} niveau="attention" role="alert" titre={a.message} />
            ))}

          <div className="barre-filtres" style={{ padding: 0 }}>
            <div className="ligne" style={{ gap: 8, flexWrap: 'wrap' }} role="group" aria-label="Étapes affichées">
              <button type="button" className="chip" aria-pressed={filtre === 'toutes'} onClick={() => setFiltre('toutes')}>
                Toutes les étapes
              </button>
              {ETAPES.map((e) => (
                <button key={e} type="button" className={`chip etape-${e}`} aria-pressed={filtre === e} onClick={() => setFiltre(e)}>
                  <span className="pastille-couleur" aria-hidden="true" /> {ETAPE_INFO[e].label}
                </button>
              ))}
            </div>
            <div className="legende">
              <span>
                <span className="meme-foyer-ico" aria-hidden="true">
                  ⚭
                </span>{' '}
                
                même voiture
              </span>
              <span>
                <span className="carre recroise" aria-hidden="true" /> se recroisent
              </span>
            </div>
          </div>

          {etapesAffichees.map((e) => {
            const ge = groupes.filter((g) => g.etape === e);
            const hotesEtape = ge.map((g) => ({ id: g.hoteFoyerId, nom: parId.get(g.hoteFoyerId)?.nom ?? '?' }));
            return (
              <section key={e} className="section-etape" aria-label={ETAPE_INFO[e].label}>
                <PastilleEtape etape={e} variante="numero" grande detail={`${heureCourte(horaires[e])} · ${ge.length} table${ge.length > 1 ? 's' : ''}`} />
                <div className="grille-groupes">
                  {ge.map((g) => (
                    <CarteGroupe
                      key={g.hoteFoyerId}
                      groupe={g}
                      foyers={parId}
                      score={score}
                      plage={plage}
                      deplaces={echange ? new Set([...echange.deplaces].filter((k) => k.startsWith(`${e}:`)).map((k) => k.slice(e.length + 1))) : undefined}
                      autresHotes={hotesEtape}
                      onDeplacer={(foyerId, vers) => deplacer(e, foyerId, vers)}
                      onDeposer={(foyerId, vers) => deplacer(e, foyerId, vers)}
                    />
                  ))}
                </div>
              </section>
            );
          })}

          <section className="carte matrice-section" aria-labelledby="matrice">
            <div className="entete-page" style={{ alignItems: 'center' }}>
              <div>
                <h2 id="matrice" style={{ fontSize: 20 }}>
                  Qui rencontre qui
                </h2>
                <p className="texte-muet" style={{ fontSize: 13 }}>
                  Chaque case : l'étape où deux personnes partagent la même table.
                </p>
              </div>
              <button type="button" className="btn" aria-expanded={matriceOuverte} onClick={() => setMatriceOuverte(!matriceOuverte)}>
                {matriceOuverte ? 'Replier ⌃' : 'Déplier ⌄'}
              </button>
            </div>
            {matrice ? (
              <>
                <div className="legende matrice-legende">
                  {ETAPES.map((e) => (
                    <span key={e} className={`etape-${e}`}>
                      <span className="carre stage" aria-hidden="true" /> {ETAPE_INFO[e].label}
                    </span>
                  ))}
                  <span>
                    <span className="carre double" aria-hidden="true" /> deux étapes
                  </span>
                  <span>
                    <span className="carre foyer" aria-hidden="true" />  même voiture
                  </span>
                  <span>
                    <span className="carre vide" aria-hidden="true" /> pas de rencontre
                  </span>
                </div>
                <div className="matrice-defilement">
                  <table className="matrice">
                    <thead>
                      <tr>
                        <th scope="col">
                          <span className="sr-only">Personne</span>
                        </th>
                        {matrice.personnes.map((p) => (
                          <th key={p.id} scope="col" title={p.nom} abbr={p.nom}>
                            {initiales(p.nom)}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {matrice.personnes.map((p) => {
                        const enDouble = matrice.personnes.some((q) => q.foyerId !== p.foyerId && matrice.rencontres(p.id, q.id).length > 1);
                        return (
                          <tr key={p.id}>
                            <th scope="row" className={enDouble ? 'en-double' : ''}>
                              {p.nom}
                            </th>
                            {matrice.personnes.map((q) => {
                              if (p.id === q.id) return <td key={q.id} className="soi" />;
                              if (p.foyerId === q.foyerId) return <td key={q.id} className="foyer" title={`${p.nom} et ${q.nom} : même voiture`} />;
                              const etapes = matrice.rencontres(p.id, q.id);
                              if (etapes.length === 0) return <td key={q.id} className="vide" title={`${p.nom} et ${q.nom} : pas de rencontre`} />;
                              if (etapes.length > 1) return <td key={q.id} className="double" title={`${p.nom} et ${q.nom} : ${etapes.map((x) => ETAPE_INFO[x].label.toLowerCase()).join(' et ')}`} />;
                              return <td key={q.id} className={`stage etape-${etapes[0]}`} title={`${p.nom} et ${q.nom} : ${ETAPE_INFO[etapes[0]!].label.toLowerCase()}`} />;
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            ) : null}
          </section>
        </>
      ) : null}
    </div>
  );
}
